import { Form, notification } from 'antd';
import RecaptchaV2 from 'components/RecaptchaV2';
import Button from 'components/ui/base/button/Button';
import Checkbox from 'components/ui/base/checkbox/Checkbox';
import { TextSmall } from 'components/ui/base/typography';
import FormInput from 'components/ui/composed/formInput/FormInput';
import FormPassword from 'components/ui/composed/formPassword/FormPassword';
import { useFormik } from 'formik';
import useSession from 'hooks/useSession';
import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IndividualType } from 'store/individuals/individual';
import { LoginCredentials } from 'store/session/user';
import * as Yup from 'yup';
import {
    ForgetRemDiv,
    ForgotPassword,
    FormItemButtons,
    LoginWithButton,
    LegalInfoDiv,
    PrivacyPolicyLink,
    SignUpButton,
    SignUpDiv,
    StyledDiv,
    StyledItem,
    TermsAndConditionsLink,
    Title,
} from './SignInForm.styles';
import CustomerSelectionModal from './CustomerSelectionModal';
import { ListPayload, ListResponse } from 'core/http/response';
import { Customer } from 'store/customers/customer';
import axiosClient from 'config/axios';
import config from 'config/config';
import useGlobalOverlay from 'hooks/useGlobalOverlay';

const getAccessibleCustomers = () =>
    axiosClient.get<ListResponse<Customer>>(`${config.cmsUrl}/customers/tenants`).then(({ data }) => data.payload);

const formValidation = Yup.object().shape({
    email: Yup.string().email('Email is invalid.').required('Email is missing.'),
    password: Yup.string().required('Password is missing.'),
});

const SignInForm: FC = () => {
    const { t } = useTranslation('login');
    const { signIn, isLoading } = useSession();
    const navigate = useNavigate();
    const [showRecaptchaCheckbox] = useState<boolean>(false);
    const { userInfo } = useSession();

    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();

    const [credentials, setCredentials] = useState<{ email: string; password: string; remember: boolean }>();
    const [customers, setCustomers] = useState<ListPayload<Customer>>();
    const [customerSelectionModalShown, setCustomerSelectionModalShown] = useState(false);
    const showCustomerSelectionModal = useCallback(() => setCustomerSelectionModalShown(true), []);
    const hideCustomerSelectionModal = useCallback(() => setCustomerSelectionModalShown(false), []);

    const formik = useFormik({
        initialValues: {
            email: '',
            password: '',
            remember: false,
        },
        validateOnMount: true,
        validationSchema: formValidation,
        enableReinitialize: true,
        onSubmit: (values) => handleSubmit(values),
    });

    useEffect(() => {
        const checkIfClickedEnterKey = (event: any) => {
            if (event.code === 'Enter') {
                formik.handleSubmit();
            }
        };
        document.addEventListener('keydown', checkIfClickedEnterKey);
        return () => {
            document.removeEventListener('keydown', checkIfClickedEnterKey);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleLoginWithGoogle = () => {
        window.location.href = `${process.env.REACT_APP_BASE_URL}${config.authUrl}/session/google`;
    };

    const handleLoginWithAzure = () => {
        window.location.href = `${process.env.REACT_APP_BASE_URL}${config.authUrl}/session/azure`;
    };

    const handleSubmit = async (credentials: LoginCredentials) => {
        showGlobalOverlay({ type: 'LoadingOverlay', payload: { message: 'Logging in...' } });
        // TODO: Uncomment this when recaptcha is ready
        // window.grecaptcha.ready(async () => {
        //     let token = await window.grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY_V3, {
        //         action: 'signin',
        //     });

        //     // implement backend validation for recaptcha v3 here
        //     // use v2 as a fallback if v3 response doesn't work out
        //     if (showRecaptchaCheckbox === false) {
        //         setShowRecaptchaCheckbox(true);
        //         return;
        //     }
        //     token = window.grecaptcha.getResponse();

        //     const session = await signIn({ ...credentials, recaptcha: token });
        //     if (session.user.role === IndividualType.DRIVER) {
        //         navigate('/mobile/home');
        //     } else {
        //         navigate('/home');
        //     }
        // });

        // TODO: Remove this when recaptcha is ready
        signIn(credentials)
            .then((session) => {
                if (session.user.role === IndividualType.SUPER_ADMIN) {
                    setCredentials(credentials);
                    getAccessibleCustomers()
                        .then((data) => {
                            setCustomers(data);
                            showCustomerSelectionModal();
                        })
                        .catch(() => {
                            notification.error({ message: 'Could not get accessible customers!' });
                        });
                    return;
                }

                if (session.user.role === IndividualType.DRIVER) {
                    navigate('/mobile/home');
                    return;
                }

                navigate('/home');
            })
            .catch(() => {
                notification.error({ message: 'Could not log in!' });
            })
            .finally(() => {
                hideGlobalOverlay();
            });
    };

    const superAdminSignIn = useCallback(
        (customerId: string) => {
            if (!userInfo || !credentials) return;
            showGlobalOverlay({ type: 'LoadingOverlay', payload: { message: 'Logging in...' } });

            return signIn({ ...credentials, customerId })
                .then(() => {
                    notification.success({ message: 'Successfully logged in!' });
                    setCredentials(undefined);
                    if (customerSelectionModalShown) hideCustomerSelectionModal();
                    navigate('/home');
                })
                .catch(() => {
                    notification.error({ message: 'Could not log in!' });
                })
                .finally(() => {
                    hideGlobalOverlay();
                });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [credentials, customerSelectionModalShown, hideCustomerSelectionModal, userInfo]
    );

    return (
        <>
            <Title>{t('loginTitle')}</Title>
            <StyledDiv>
                <Form>
                    <FormInput
                        autoFocus
                        label={t('emailLabel')}
                        fieldProps={formik.getFieldProps('email')}
                        fieldMeta={formik.getFieldMeta('email')}
                    />
                    <FormPassword
                        icon
                        label={t('passwordLabel')}
                        fieldProps={formik.getFieldProps('password')}
                        fieldMeta={formik.getFieldMeta('password')}
                    />
                    <ForgetRemDiv>
                        <StyledItem name="remember" valuePropName="checked" noStyle>
                            <Checkbox
                                checked={formik.getFieldProps('remember').value as boolean}
                                onChange={() => {
                                    const values = { ...formik.values };
                                    values.remember = !formik.values.remember;
                                    formik.setValues(values);
                                }}
                            >
                                {t('rememberMe')}
                            </Checkbox>
                        </StyledItem>
                        <ForgotPassword type="link" onClick={() => navigate('/reset')}>
                            {t('forgotPassword')}
                        </ForgotPassword>
                    </ForgetRemDiv>
                    <LegalInfoDiv>
                        <TextSmall>Read our</TextSmall>
                        <TermsAndConditionsLink onClick={() => navigate('/terms-and-conditions')}>
                            Terms & Conditions
                        </TermsAndConditionsLink>
                        <TextSmall>and</TextSmall>
                        <PrivacyPolicyLink onClick={() => navigate('/privacy-policy')}>
                            Privacy Policy
                        </PrivacyPolicyLink>
                        <TextSmall>.</TextSmall>
                    </LegalInfoDiv>
                    {showRecaptchaCheckbox ? (
                        <>
                            <RecaptchaV2 />
                            <p>You may be a bot! Please check the checkbox.</p>
                        </>
                    ) : null}
                    <FormItemButtons>
                        <Button
                            loading={isLoading}
                            block
                            type="primary"
                            size="large"
                            onClick={() => formik.handleSubmit()}
                        >
                            {t('loginTitle')}
                        </Button>
                        <LoginWithButton
                            loading={isLoading}
                            block
                            type="primary"
                            size="large"
                            onClick={() => handleLoginWithGoogle()}
                        >
                            Login with Google
                        </LoginWithButton>
                        <LoginWithButton
                            loading={isLoading}
                            block
                            type="primary"
                            size="large"
                            onClick={() => handleLoginWithAzure()}
                        >
                            Login with Azure
                        </LoginWithButton>
                    </FormItemButtons>
                </Form>
                <SignUpDiv>
                    {t('newToDDispatch')}
                    <SignUpButton
                        type="link"
                        onClick={() => {
                            navigate('/signUp');
                        }}
                    >
                        {t('signUp')}
                    </SignUpButton>
                </SignUpDiv>
            </StyledDiv>
            <CustomerSelectionModal
                visible={customerSelectionModalShown}
                onCancel={hideCustomerSelectionModal}
                customers={customers}
                onConfirm={superAdminSignIn}
            />
        </>
    );
};

export default SignInForm;
